import { z } from 'zod';
import { AxiosRequestConfig } from 'axios';
import { addedDiff, deletedDiff, updatedDiff } from 'deep-object-diff';

import _ from 'lodash';
import dayjs from 'dayjs';
import Joi from 'joi';

import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import duration from 'dayjs/plugin/duration';
import minMax from "dayjs/plugin/minMax";

import config from './config';

import { PREFIXABLE_MODULES } from '../data/modules';
import { ALPHABET, ANOMALY_SUMMARY_OPTIONS, ANOMALY_TYPE, APP_DATE_FORMAT, APPLICATION_TYPE_PREFIX, BYTE_SIZE, DAMAGE_CLASS_TO_RISK_FACTOR, DAYJS_VALID_FORMAT, EDITABLE_PROJECT_STATUS, EQUIPMENT_SUMMARY_KEY, EXCEL_APPLICATION_EXTENSION, EXCEL_APPLICATION_TYPE, EXTENDABLE_MODULES, IMAGE_TYPE_PREFIX, IMAGE_UPLOAD_FORMATS, MATCH_TYPES, MAX_PAGE_WORD_COUNT, OFFLINE_TOKEN, PDF_APPLICATION_EXTENSION, PDF_APPLICATION_TYPE, PIPING_SUMMARY_KEY, RISK_RATING_TYPES } from './constants';
import { LNG_ID, SHELL_ID } from './ids';
import { MappedAnomalyOption } from '../hooks/useMappedAnomalies';

import * as models from './models';
import * as data from './data';

dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(relativeTime);
dayjs.extend(duration);
dayjs.extend(minMax);

const calculateWallLossValue = (nominalThickness: number, minThickness: number) => {
	if (minThickness >= nominalThickness) {
		return 0;
	}

	return nominalThickness - minThickness;
};

const calculateWallLossPercentage = (nominalThickness: number, minThickness: number) => {
	const parsedNominalThickness = parseFloat(nominalThickness.toString());

	if (isNaN(parsedNominalThickness)) {
		return 'N/A';
	}

	return ((calculateWallLossValue(parsedNominalThickness, minThickness) / parsedNominalThickness) * 100).toFixed(2);
};

const getAnomalyId = (lineId: number, anomalyIndex: number) => {
	return lineId + '.' + anomalyIndex;
};

const getMinimumDate = (dates: string[]) => {
	return dayjs.min(dates.map((date) => dayjs(date)))?.format(APP_DATE_FORMAT);
};

const getMaximumDate = (dates: string[]) => {
	return dayjs.max(dates.map((date) => dayjs(date)))?.format(APP_DATE_FORMAT);
};

const getYearFromDate = (date: string) => {
	return dayjs(date).year();
};

const compareSimilarValues = (baseValue: string, comparatorValue: string) => {
	return baseValue.toString().toLowerCase().indexOf(comparatorValue.toLowerCase()) !== -1
};

const getAnomalyFieldColor = (value: string) => {
	const NEGATIVE_OPTIONS = ['anomaly', 'not in working condition', 'yes', 'light pitting', 'moderate pitting', 'pitting corrosion', 'passive pitting', 'active pitting'];
	const backgroundColor = (value && NEGATIVE_OPTIONS.includes(value.toLowerCase().trim())) ? '#ffcccc' : '#cdffc5';

	return { backgroundColor };
};

const getIsAnomalyType = (project: models.Project) => {
	if (!project) return false;

	return (project.organisationId === LNG_ID) || (project.organisationId === SHELL_ID && getIsAfter(project.createdAt, '2025-02-02'));
};

const getIsAnomalyWithCMD = (project: models.Project) => {
	if (project) {
		const supportedReports = [
			config.supportedTemplates.exxonMobilFVIEquipment,
			config.supportedTemplates.exxonMobilFVIPiping,
			config.supportedTemplates.exxonMobilFVIStructure,
			config.supportedTemplates.exxonMobilFVITank,
			config.supportedTemplates.gre,
			config.supportedTemplates.exxonMobilUTMEquipment,
			config.supportedTemplates.exxonMobilUTMPiping,
			config.supportedTemplates.exxonMobilUTMStructure,
		];

		return (project.organisationId === SHELL_ID && getIsAfter(project.createdAt, '2025-04-12') && supportedReports.includes(project.inspectionTypeId));
	}

	return false;
};

const formatUniquePublicID = (fileName: string) => {
	const removeUnpermittedCharacters = (fileName: string) => {
		let specialCharacters = '#><+%&?/- ';
		let updatedFileName = fileName;

		for (let i = 0; i < specialCharacters.length; i++) {
			const characterMatchIndex = fileName.indexOf(specialCharacters[i]);
			if (characterMatchIndex === -1) continue

			updatedFileName = updatedFileName.replaceAll(fileName.charAt(characterMatchIndex), '_')
		}

		return updatedFileName.trim();
	}

	const removeExtensionFromFileName = (fileName: string) => {
		const allSupportedFileTypes = IMAGE_UPLOAD_FORMATS.map((format) => getFileTypeAffix(format))
			.concat([PDF_APPLICATION_EXTENSION, EXCEL_APPLICATION_EXTENSION]);

		const fileTypeMatch = allSupportedFileTypes.find((fileType) => fileName.toLowerCase().indexOf(fileType.toLowerCase()) !== -1);
		if (fileTypeMatch) {
			const parts = fileName.split(`.${fileTypeMatch}`);
			return parts[0];
		}

		return fileName;
	};

	const formattedFileName = removeExtensionFromFileName(removeUnpermittedCharacters(fileName));
	if (formattedFileName.toLowerCase().indexOf('_rsg_') !== -1) {
		return formattedFileName;
	}

	return new Date().getTime() + '_' + formattedFileName;
};

const getInternalReviewDetails = (project: models.Project) => {
	const getDate = (date: string | Date): string => {
		return date ? formatTime(date, APP_DATE_FORMAT) : '';
	};

	if (project.report?.internalReviewerName) {
		return {
			reviewerName: project.report.internalReviewerName,
			reviewerQualifications: project.report.internalReviewerDesignation,
			reviewDate: getDate(project.report.internalReviewDate),
			reviewerSignature: project.report.internalReviewerSignature,
		}
	}

	if (project.report?.approvedByName) {
		return {
			reviewerName: project.report.approvedByName,
			reviewerQualifications: project.report.approvedByDesignation,
			reviewDate: getDate(project.report.approvedDate),
			reviewerSignature: project.report.approvedBySignature,
		}
	}

	return {
		reviewerName: project.projectActivator,
		reviewerQualifications: project.projectActivatorDesignation,
		reviewDate: getDate(project.report?.updatedAt ?? new Date()),
		reviewerSignature: project.projectActivatorSignature,
	}
};

const getCanCreateReport = (projectDetails: models.Project | null, user: models.User): boolean => {
	if (!projectDetails || projectDetails.isImported) return false;

	if (projectDetails.report && projectDetails.report.approved) {
		return false;
	}

	if (projectDetails.isActive) {
		const isAssignedSupervisor: boolean = projectDetails.supervisorId === user.id;
		if (isAssignedSupervisor && user.role.manageReports) {
			return EDITABLE_PROJECT_STATUS.includes(projectDetails.projectStatus as any);
		}

		return user.role.reviewReports;
	}

	return false
};

const getChunkedData = (data: any[], limit: number): any[][] => {
	return _.chunk(data, limit);
};

const getChunkedAnomalies = (inspectedItem: { anomalyItems: models.NewAnomalyItem[]; }, limit: number) => {
	const inspectedItemWithoutAnomalies = _.omit(inspectedItem, ['anomalyItems']);

	return getChunkedData(inspectedItem.anomalyItems, limit).map((chunkedAnomalies) => ({ ...inspectedItemWithoutAnomalies, anomalyItems: chunkedAnomalies }));
};

const getItemsChunkedData = <T>(items: T[], firstPageLength: number, subsequentPageLength: number) => {
	// If the readings are greater than the default count per page, chunk the first page by the default and render the others according to preference count.

	if (items.length > firstPageLength) {
		const firstChunk = items.slice(0, firstPageLength);
		const chunks = getChunkedData(items.slice(firstPageLength), subsequentPageLength);

		return [firstChunk, ...chunks];
	};

	return getChunkedData(items, firstPageLength);
};

const getIsAfter = (startDate: Date | string, endDate: Date | string) => {
	return dayjs(startDate).isAfter(dayjs(endDate));
};

const getIsBefore = (startDate: Date | string, endDate: Date | string) => {
	return dayjs(startDate).isBefore(dayjs(endDate));
};

const getIsSameOrBefore = (startDate: Date | string, endDate: Date | string) => {
	return dayjs(startDate).isSameOrBefore(dayjs(endDate));
};

const getIsSameOrAfter = (startDate: Date | string, endDate: Date | string) => {
	return dayjs(startDate).isSameOrAfter(dayjs(endDate));
};

const getDaysBeforeFromCurrentDate = (daysBack: number) => {
	const dateBack = dayjs.duration(daysBack, 'd');
	return dayjs().subtract(dateBack);
};

const getNumberOfMeasurementsForPECScan = (pecScan: models.PECScanData) => {
	const numberOfMeasurementsInScan: number = pecScan.readings.data.reduce((a, b) => {
		const itemWithoutCircValue = _.omit(b, ['srNo']);
		const measurements = Object.keys(itemWithoutCircValue).length;

		return a + measurements;
	}, 0);

	return numberOfMeasurementsInScan;
};

const checkImageIsBase64 = (src: string) => {
	return src.indexOf(';base64,') !== -1;
};

const convertBytesToMB = (sizesInBytes: number) => {
	return sizesInBytes / BYTE_SIZE;
};

const checkItemIsArray = (item: unknown) => {
	return item instanceof Array;
};

const checkPIDSupportedForInspection = (inspectionTypeId: string): boolean => {
	return false;
};

const checkTemplateIsSupported = (templateId: string) => {
	const { supportedTemplates } = config;

	const supportedTemplateIds = Object.keys(supportedTemplates).map(
		(key) => supportedTemplates[key as keyof typeof supportedTemplates]
	);

	return supportedTemplateIds.indexOf(templateId) !== -1;
};

const checkAnomalyChecklistComplete = (inspectedItem: { checklistItems: models.FVIPipingItem['checklistItems'] }) => {
	return inspectedItem.checklistItems.every((item) => item.assessment);
};

const concatClassNames = (classNames: string[]) => {
	return classNames.join(' ');
};

const mapAnomalyItems = (items: models.ItemWithAnomaly[]) => {
	const ANOMALY_ITEMS_PER_SECTION = 7;
	const SUBGROUP_ITEMS_PER_PAGE = 3;

	let group: models.ItemWithAnomaly[][] = [];
	let subgroup: models.ItemWithAnomaly[] = [];

	for (const item of items) {
		if (item.anomalyItems.length <= ANOMALY_ITEMS_PER_SECTION) subgroup.push(item);
		else {
			for (const anomalyChunk of getChunkedData(item.anomalyItems, ANOMALY_ITEMS_PER_SECTION)) {
				const newSubgroupItem = { ...item, anomalyItems: anomalyChunk };
				if (subgroup.length < SUBGROUP_ITEMS_PER_PAGE) subgroup.push(newSubgroupItem);
				else {
					group.push(subgroup)
					subgroup = [newSubgroupItem];
				}
			}
		}

		if (subgroup.length >= SUBGROUP_ITEMS_PER_PAGE) {
			group.push(subgroup)
			subgroup = [];
		}
	}

	if (subgroup.length > 0) {
		group.push(subgroup);
		subgroup = [];
	}

	return group;
};

const copyUpdatedData = (oldData: any, newData: any) => {
	const oldDataCopy = { ...oldData };
	const newDataCopy = { ...newData };

	let description = '';

	const customizer = (
		value: any,
		srcValue: any,
		key: string,
		object: any,
		source: any
	) => {
		if (!_.isEqual(value, srcValue)) {
			if (!_.isEmpty(addedDiff(value, srcValue))) {
				description += `New items were added to ${key.toUpperCase()}. `;
			}

			if (!_.isEmpty(deletedDiff(value, srcValue))) {
				description += `Some items were deleted from ${key.toUpperCase()}. `;
			}

			if (!_.isEmpty(updatedDiff(value, srcValue))) {
				description += `${key.toUpperCase()} has been updated.`;
			}
		}

		if (_.isArray(srcValue)) {
			return srcValue;
		}
	};

	return [_.mergeWith(oldDataCopy, newDataCopy, customizer), description];
};

const extractBase64String = (imageString: string) => {
	const parts = imageString.split(';base64,');
	return parts.length > 1 ? parts[1] : parts[0];
};

const formatAddedDaysFromTimestamp = (timestamp: number, addedDays: number) => {
	return dayjs(timestamp).add(addedDays, 'day').fromNow();
};

const formatDigits = (digits: number): string | null => {
	return digits.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
};

const formatTime = (time: string | number | Date, format?: string) => {
	const actualFormat: string = format ?? APP_DATE_FORMAT + ' HH:mm:ss';
	return dayjs(time).format(actualFormat);
};

const formatTimeWithRelativeStamp = (time: string | number | Date) => {
	return getRelativeTime(time) + ' at ' + formatTime(time);
};

const getAnomalyStartNumber = (data: { anomalyItems: models.AnomalyItem[] | models.NewAnomalyItem[] }[]) => {
	const lastLineNumber = _.findLast(data, (item) => item.anomalyItems.length > 0);
	if (lastLineNumber) {
		const lastAnomaly = _.last<models.AnomalyItem | models.NewAnomalyItem>(lastLineNumber.anomalyItems);
		if (lastAnomaly) return lastAnomaly.id + 1;
	}

	return 1;
};

const getChartLabelFromContext = (ctx: any): string => {
	const chartValues: number[] = ctx.dataset.data;
	const total: number = chartValues.reduce((a: number, c: number) => a + c, 0);
	const percentage: number = (ctx.parsed / total) * 100;

	return `${ctx.label} (${ctx.parsed}): ${percentage.toFixed(2) + '%'}`;
};

const getValueToDecimalPlaces = (value: number, decimalPlaces: number) => {
	return (isNaN(value)) ? value : parseFloat(value.toString()).toFixed(decimalPlaces);
}

const getCachedDataFromException = (ex: any) => {
	return ex?.response?.data;
};

const getDateFromString = (dateString: string) => {
	return dayjs(formatTime(dateString, DAYJS_VALID_FORMAT)).toDate();
};

const getChunkedAnomalySummaryItems = (inspectionItem: { anomalyItems: models.NewAnomalyItem[] }) => {
	const allAnomalyDescriptions = inspectionItem.anomalyItems.map((anomaly) => anomaly.description).flat();
	if (allAnomalyDescriptions.length > 15) {
		const firstInspectionItem = inspectionItem.anomalyItems.slice(0, 10);
		const subsequentInspectionItems = getChunkedData(inspectionItem.anomalyItems.slice(10), 30);

		return [firstInspectionItem, ...subsequentInspectionItems];
	}

	return getChunkedData(inspectionItem.anomalyItems, inspectionItem.anomalyItems.length);
};

const getExtractedInspectionName = (inspectionTypeLabel: string) => {
	const isEnabled = false;

	if (isEnabled) {
		const valueToReplace = 'new';
		const updatedValue = inspectionTypeLabel.toLowerCase().replace(valueToReplace, '').trim();

		return _.capitalize(updatedValue);
	}

	return inspectionTypeLabel;
}

const getPrefixedModule = (module: string): string => {
	if (_.includes(PREFIXABLE_MODULES, module)) {
		return `Module ${module}`
	}

	return module;
}

const getRelativeTime = (time: string | number | Date) => {
	return dayjs(time).fromNow();
};

const getRiskRatingData = (riskRatingData: any[]) => {
	const levels: models.RiskLevel[] = data.getRiskLevels();

	const labels = _.map(levels, 'level');
	const backgroundColor = _.map(levels, 'backgroundColor');

	const breakdownData: number[] = labels.map(
		(l: string) =>
			_.filter(
				riskRatingData,
				(r: models.RiskRatingItem) => r.level.toLowerCase() === l
			).length
	);

	return { breakdownData, backgroundColor, labels };
};

const getStyleFromRisk = (item: any): any => {
	const level = getRiskLevelFromName(item.level.toLowerCase());
	if (!level) return null;

	return { backgroundColor: level.backgroundColor, color: level.textColor };
};

const getRatingIsMajor = (rating: number): boolean => {
	return rating >= 80;
};

const getRatingIsModerate = (rating: number): boolean => {
	return rating >= 50;
};

const getRatingIsMinor = (rating: number): boolean => {
	return rating < 50;
};

const getOptimizedImageURL = (source: string) => {
	if (source && !config.preferS3ToCloudinary) {
		const quality = 'q_auto:low/';
		if (source.indexOf(quality) !== -1) return source;

		const target = '/upload/';
		const index = source.indexOf(target);
		if (index === -1) return source

		const length = index + target.length;
		const url = `${source.slice(0, length)}${quality}${source.slice(length)}`;

		return url;
	}

	return source;
};

const getCriticalityFromRating = (rating: number) => {
	if (getRatingIsMajor(rating)) return 'major';
	if (getRatingIsModerate(rating)) return 'moderate';
	return 'minor';
};

const getWallThicknessValue = (nominalThickness: number, minimumThickness: number) => {
	return (((nominalThickness - minimumThickness) / nominalThickness) * 100);
}

const paginate = <T>(items: T[], currentPage: number, totalPages: number) => {
	const startIndex: number = (currentPage - 1) * totalPages;
	return items.slice(startIndex).filter((_, index) => index < totalPages);
};

const removeKeywordFromReference = (keyWord: string, reference: string) => {

	const index = reference.indexOf(keyWord);
	if (index !== -1) return reference.slice(0, index) + reference.slice(index + keyWord.length);

	return reference;
};

const replaceKeywordInReference = (keyWord: string, replacement: string, reference: string) => {
	const index = reference.indexOf(keyWord);
	if (index !== -1) return reference.slice(0, index) + replacement + reference.slice(index + keyWord.length);

	return reference;
};

const validateFileLength = (files: File[], maxLength: number): boolean => {
	return files.length <= maxLength;
};

const validateFileSize = (files: File[], maxSize: number): boolean => {
	const isValid: boolean = files.every((file: File) => file.size <= maxSize);
	return isValid;
};

const validateFileType = (files: File[], types: string[]): boolean => {
	return files.every((f: File) => {
		return types.indexOf(f.type) !== -1;
	});
};

const validateWorkOrderNumber = (workOrderNumber: string) => {
	const positiveNumberSchema = z
		.union([z.string(), z.number()])
		.refine((val) => (typeof val === "string") ? val.toLowerCase() !== "n/a" : true, { message: "Value cannot be N/A" })
		.transform((val) => (typeof val === "string" ? Number(val) : val))
		.refine((num) => !isNaN(num), { message: "Must be a valid number" })
		.refine((num) => num > 0, { message: "Must be a positive number" });

	const validation = positiveNumberSchema.safeParse(workOrderNumber);
	return validation.success;
};

const getTimeDifference = (date: number | string | Date, format: any) => {
	const now: dayjs.Dayjs = dayjs();
	const storedTime: dayjs.Dayjs = dayjs(date);

	return now.diff(storedTime, format);
};

const getIsExpired = (date: number | string | Date, format: any, time: number) => {
	return getTimeDifference(date, format) > time;
};

const getIsExcelFile = (file: File) => {
	return file.type === EXCEL_APPLICATION_TYPE;
}

const getIsPDFFile = (file: File) => {
	return file.type === PDF_APPLICATION_TYPE;
}

const getPageRange = (size: number) => {
	return _.range(1, size + 1);
};

const getValueRange = (startRange: number, endRange: number) => {
	return _.range(startRange, endRange + 1);
};

const getFileUploadTypes = (): string[] => {
	return IMAGE_UPLOAD_FORMATS.concat([PDF_APPLICATION_TYPE]);
};

const getErrorMessage = (ex: any) => {
	return ex.response?.data?.message || ex.message;
};

const getPIDImages = (images: string[]): models.PDFImageProps[] => {
	return images.slice(0, 1).map((image: string) => ({
		refNo: _.uniqueId(),
		src: image,
		caption: 'P&ID Image',
	}));
};

const getRandomColor = (): string => {
	const colors: string[] = [
		'#ffa600',
		'#003f5c',
		'#2f4b7c',
		'#665191',
		'#a05195',
		'#d45087',
		'#f95d6a',
		'#ff7c43',
		'#ffa600',
	];

	return colors[Math.floor(Math.random() * colors.length)];
};

const getRiskLevelFromName = (name: string) => {
	return data.getRiskLevels().find((l: models.RiskLevel) => l.level === name);
};

const getInitials = (text: string): string => {
	if (!text) return '';

	return text
		.split(' ')
		.map((word) => word.charAt(0).toUpperCase())
		.join('');
};

const getItemStyle = (item: Pick<models.RiskRatingItem, 'level'>): any => {
	const level = getRiskLevelFromName(item.level.toLowerCase());
	if (!level) return null;

	return {
		backgroundColor: level.backgroundColor,
		color: level.textColor,
		textAlign: 'left',
	};
};

const getRiskFactorEquivaluent = (riskFactor: string) => {
	if (!riskFactor) return null;

	const riskFactorClass = _.get(DAMAGE_CLASS_TO_RISK_FACTOR, riskFactor.toLowerCase());
	return riskFactorClass;
}

const getPCNLRiskFactorClassification = (riskFactor: string) => {
	const riskFactorClass = getRiskFactorEquivaluent(riskFactor);
	if (!riskFactorClass) return null;

	const options = data.getStructureAnomalyClassOptions().filter((option) => riskFactorClass.classes.includes(option.id));
	const selectedRiskFactorClass: { color: string; recommendations: models.CoverDataItem[]; descriptions: models.CoverDataItem[] } = {
		color: riskFactorClass.color,
		recommendations: [],
		descriptions: [],
	};

	for (const option of options) {
		selectedRiskFactorClass.descriptions = [...selectedRiskFactorClass.descriptions, ...option.descriptions];
		selectedRiskFactorClass.recommendations = [...selectedRiskFactorClass.recommendations, ...option.recommendations];
	}

	return selectedRiskFactorClass;
};

const getAnomalyClassStyle = (item: Pick<models.NewAnomalyItem, 'class'>): any => {
	const styles = {
		backgroundColor: '#FFFFFF',
		color: '#000000',
		textAlign: 'center',
	};

	const classMatch = _.find(data.getPipingAnomalyClassOptions(), ['id', +item.class]);
	if (classMatch) styles.backgroundColor = classMatch.color;

	return styles;
};

const getAnomalyTypeStyle = (item: { class: string | number }) => {
	const styles = {
		backgroundColor: '#FFFFFF',
		color: '#000000',
		textAlign: 'center',
	};

	const options = data.getAnomalyTypeClassOptions();
	const optionMatch = options.find((option) => matchPhrases(option.id, item.class.toString()));
	if (optionMatch) styles.backgroundColor = optionMatch.color;

	return styles;
};

const getLNGAnomalyClassStyle = (className: string): React.CSSProperties => {
	const styles = {
		backgroundColor: '#FFFFFF',
		color: '#000000',
		textTransform: 'capitalize' as const,
		textAlign: 'center' as const,
	};

	const anomalyTypeClassOptions = data.getAnomalyTypeClassOptions();
	const style = anomalyTypeClassOptions.find((anomalyClass) => anomalyClass.id === className);
	if (style) styles.backgroundColor = style.color;

	return styles;
};

const getTotalScanLength = (start: number, end: number) => {
	return start !== undefined && end !== undefined ? +start + +end : 'N/A';
};

const getPCNLAnomalyStyle = (item: Pick<models.PCNLAnomalyItem, 'riskFactor'>): React.CSSProperties => {
	if (!item.riskFactor) return {};

	const riskFactorClass = _.get(DAMAGE_CLASS_TO_RISK_FACTOR, item.riskFactor.toLowerCase());
	if (!riskFactorClass) return {};

	return {
		backgroundColor: riskFactorClass.color,
		color: '#000000',
		textTransform: 'capitalize',
		textAlign: 'center',
	}
};

const getShowLastLineOption = (inspectionItems: { hasPID: string; }[], inspectionItemIndex: number) => {
	if (inspectionItemIndex > 0 && inspectionItems[inspectionItemIndex - 1]) {
		const previousInspectionItem = inspectionItems[inspectionItemIndex - 1];
		return !matchPhrases(previousInspectionItem.hasPID, 'no');
	}

	return false;
};

const getMaxClassFromInspectedItem = (pipingItem: { anomalyItems: models.NewAnomalyItem[] }) => {

	if (pipingItem.anomalyItems.length > 0) {
		const anomalyClasses = pipingItem.anomalyItems.map((anomaly) => anomaly.class);
		const isDamageClass = pipingItem.anomalyItems.every((anomaly) => !isNaN(anomaly.class));

		if (isDamageClass) {
			return Math.max(...anomalyClasses);
		}
	}

	return 0;
};

const getMaxAnomalyType = (inspectionItem: { anomalyItems: models.NewAnomalyItem[]; }) => {
	if (inspectionItem.anomalyItems.length > 0) {
		const severityOrder = [
			ANOMALY_TYPE.MAJOR.ID,
			ANOMALY_TYPE.MODERATE.ID,
			ANOMALY_TYPE.MINOR_TO_MODERATE.ID,
			ANOMALY_TYPE.MINOR.ID,
			ANOMALY_TYPE.NONE.ID,
		];

		for (const severity of severityOrder) {
			if (inspectionItem.anomalyItems.some((anomaly) => anomaly.class.toString() === severity)) {
				return severity;
			}
		}
	}

	return 'N/A';
};

const getMaxRatingFromInspectedItem = (item: { pcnlItemName: string; anomalyItems: models.PCNLAnomalyItem[]; riskAssessment: models.PCNLRiskAssessmentItem[]; }) => {
	const anomalyClasses = matchPhrases(item.pcnlItemName, 'risk assessment') ? item.riskAssessment.map((risk) => risk.riskRating) : item.anomalyItems.map((anomaly) => anomaly.riskFactor);

	if (anomalyClasses.indexOf(RISK_RATING_TYPES.SEVERE) !== -1) return RISK_RATING_TYPES.SEVERE;
	if (anomalyClasses.indexOf(RISK_RATING_TYPES.HIGH) !== -1) return RISK_RATING_TYPES.HIGH;
	if (anomalyClasses.indexOf(RISK_RATING_TYPES.MEDIUM) !== -1) return RISK_RATING_TYPES.MEDIUM;

	return RISK_RATING_TYPES.LOW;
};

const getLabelFromInspectedItems = (inspectedItems: { anomalyItems: models.NewAnomalyItem[] }[], keyword: string) => {
	const pipingItemsWithClass = inspectedItems.map((inspectedItem) => ({ ...inspectedItem, class: inspectedItem.anomalyItems.every((anomaly) => isNaN(anomaly.class)) ? getMaxAnomalyType(inspectedItem) : getMaxClassFromInspectedItem(inspectedItem) }));
	const groupedAnomalies = _.groupBy(pipingItemsWithClass, 'class');

	const label = Object.keys(groupedAnomalies).map((key) => `${groupedAnomalies[key].length} ${keyword} ${groupedAnomalies[key].length > 1 ? 'have' : 'has'} been classified as level ${key}`).join(', ');
	return label + '.';
};

const getExecutiveSummaryAffix = (inspectedItems: { anomalyItems: models.NewAnomalyItem[] }[], unit: string) => {
	const pluralized = inspectedItems.length > 1 ? 'were' : 'was';

	return `<br > Total ${inspectedItems.length} ${unit} ${pluralized} reported in this report. ${getLabelFromInspectedItems(inspectedItems, unit)} Full details of findings are as referenced in the below pages.`
};

const getSplitData = <T>(data: T[], limit: number): T[][] => {
	return _.chunk(data, limit);
};

const getConcreteDataRange = (start: number, end: number) => {
	const CUT_OFF_RANGE = 13;
	const chunkedData = getChunkedData(getValueRange(start, end), CUT_OFF_RANGE);

	return chunkedData.map((chunkedRange: number[]) => ({
		startRange: chunkedRange[0],
		endRange: chunkedRange[chunkedRange.length - 1],
		range: chunkedRange,
	}));
};

const getUniqueItems = (items: any[]) => {
	const unique: any = [];

	for (const item of items) if (unique.indexOf(item) === -1) unique.push(item);

	return unique;
};

const getPageDetails = (
	pageTitle: string,
	pageSubtitle?: string
): models.PDFPageDetailsProps => {
	return { pageTitle, pageSubtitle };
};

const matchPhrases = (
	phraseOne: string | undefined,
	phraseTwo: string | undefined
): boolean => {
	if (!phraseOne || !phraseTwo) return false;

	return phraseOne.toString().trim().toLowerCase() === phraseTwo.toString().trim().toLowerCase();
};

const matchObjects = (objectOne: object, objectTwo: object) => {
	return _.isEqual(objectOne, objectTwo);
};

const getFileTypeAffix = (type: string): string => {
	if (type.indexOf(IMAGE_TYPE_PREFIX) !== -1) {
		return type.split(IMAGE_TYPE_PREFIX)[1];
	}

	if (type.indexOf(APPLICATION_TYPE_PREFIX) !== -1) {
		return type.split(APPLICATION_TYPE_PREFIX)[1];
	}

	return type;
};

const getFileCaptionIsInvalid = (fileCaption: string) => {
	const formattedCaption = fileCaption.toLowerCase();

	const formatExtensions = IMAGE_UPLOAD_FORMATS.map((format) => format.split(IMAGE_TYPE_PREFIX)[1]);
	const extensionInCaption = formatExtensions.some((extension) => formattedCaption.indexOf('.' + extension) !== -1);

	return formattedCaption.startsWith('17') || extensionInCaption || formattedCaption.includes('img');
};

const getMultipleFileCaptionsNotEmpty = (files: models.PDFImageProps[]) => {
	return files.every((file) => file.caption.length > 0);
};

const getFileCaptionsUnique = (files: models.PDFImageProps[]) => {
	const trimmedFiles = files.map((file) => file.caption?.toLowerCase()?.trim());
	return _.uniq(trimmedFiles).length === files.length;
};

const getMultipleFileCaptionsValid = (files: models.PDFImageProps[]) => {
	const fileCaptionsValid = files.every((file) => !getFileCaptionIsInvalid(file.caption));
	const fileCaptionsNotEmpty = getMultipleFileCaptionsNotEmpty(files);

	return fileCaptionsValid && fileCaptionsNotEmpty;
};

const getPrefixNotFoundInSingleFile = (caption: string) => {
	return caption && caption.toLowerCase().indexOf('photo ') !== 0;
};

const getPrefixNotFoundInMultipleFiles = (files: models.PDFImageProps[]) => {
	return files.every((file) => getPrefixNotFoundInSingleFile(file.caption));
};

const getStringFromAnomalyDescription = (description: models.CoverDataItem[]): string => {
	return description.map(({ value }) => value).join('. ');
}

const renderCell = (item: any, column: models.Column, itemIndex?: number): any => {
	if (column.content) return column.content(item, itemIndex);

	return _.get(item, column.path);
};

const renderStyle = (item: any, column: models.Column) => {
	if (column.style) return column.style(item);
};

const removeAccessToken = (): AxiosRequestConfig<any> => {
	return {
		transformRequest: [
			(data: any, headers: any) => {
				delete headers.common['x-access-token'];
				return data;
			},
		],
	};
};

const saveImage = (imageSrc: string, fileName: string): void => {
	const anchor: HTMLAnchorElement = document.createElement('a');
	anchor.download = fileName;
	anchor.href = imageSrc;
	anchor.target = '_blank';
	anchor.rel = 'noopener noreferrer';

	anchor.click();
};

const saveJSONFile = (jsonFile: object, fileName: string): void => {
	const anchor: HTMLAnchorElement = document.createElement('a');
	const file = new Blob([JSON.stringify(jsonFile)], { type: 'application/json' });
	anchor.href = URL.createObjectURL(file);
	anchor.download = fileName;
	anchor.target = '_blank';
	anchor.rel = 'noopener noreferrer';

	anchor.click();
};

const sortTMLReadings = (readings: models.UTMInspection['items']) => {
	const getTMLValue = (reading: models.UTMInspection['items'][0]) => {
		if (isNaN(reading.tmlLocation)) {
			const tmlToDigit = _.difference(reading.tmlLocation.toString().split(''), ALPHABET).join('');
			return parseInt(tmlToDigit);
		};

		return +reading.tmlLocation
	};

	return _.orderBy(readings, getTMLValue, ['asc']);
};

const summarize = (text: string, limit?: number): string => {
	const actualLimit = limit ? limit : 10;
	return text.length > actualLimit
		? text.substring(0, actualLimit) + '...'
		: text;
};

const transformDivisionItem = (division: models.Division): models.SelectOption => {
	return {
		id: division._id,
		name: division.divisionName,
	};
};

const transformPageRange = (range: number): models.SelectOption[] => {
	return getPageRange(range).map((p: number) => ({ id: p, name: p }));
};

const transformRoleItem = (r: any): models.Role => {
	return {
		id: r._id,
		name: r.name,
		description: r.description,
		userType: r.userType,
		manageProjects: r.permissions[0]?.manageProjects,
		manageRoles: r.permissions[0]?.manageRoles,
		manageUsers: r.permissions[0]?.manageUsers,
		manageReports: r.permissions[0]?.manageReports,
		reviewReports: r.permissions[0]?.reviewReports,
		approveReports: r.permissions[0]?.approveReports,
		viewReports: r.permissions[0]?.viewReports,
		previewAllReports: r.permissions[0]?.previewAllReports,
		viewAssociatedReports: r.permissions[0]?.viewAssociatedReports,
	};
};

const transformProjectItem = (project: any): models.Project => {
	return {
		...project,
		report: project.report ?? {},
		facility: project.facility[0],
		organisationId: _.isArray(project.organisationId) ? project.organisationId[0]._id : project.organisationId,
		organisation: project.organisation[0],
		review: project.reviewer.length > 0 ? project.reviewer.at(-1) : {},
	};
};

const transformUserItem = (u: models.User): models.SelectOption => {
	return { id: u.id, name: u.firstName + ' ' + u.lastName };
};

const transformFacilityItem = (f: models.Facility): models.SelectOption => {
	return { id: f._id, name: f.facilityName };
};

const transformInspectionType = (type: models.InspectionType) => {
	const suffix = checkTemplateIsSupported(type._id) ? ' (Supported)' : '';

	return {
		id: type._id,
		code: type.inspectionCode,
		name: type.inspectionName + suffix,
	};
};

const transformSelectOptions = (items: models.SelectOption[]): (string | number)[] => {
	return _.uniq(items.map((i: models.SelectOption) => i.id));
};

const transformCountry = (country: models.Country) => {
	return {
		id: country.name,
		name: country.name,
		code: country.Iso2,
	};
};

const transformCityToOption = (city: string) => {
	return {
		id: city,
		name: city,
	};
};

const transformStateToOption = (state: { name: string }) => {
	return { id: state.name, name: state.name };
};

const transformPlaceSearchResult = (result: any) => {
	return result.address.freeformAddress;
};

const toTitleCase = (phrase: string | undefined): string | null => {
	if (!phrase) return null;
	let words: string[] = phrase.split(' ');

	for (let i = 0; i < words.length; i++) {
		let word = words[i];
		words[i] = word.charAt(0).toUpperCase() + word.substring(1).toLowerCase();
	}

	return words.join(' ');
};

const formatAnomalyText = (anomalyItems: models.SelectOption[]): string => {
	let description: string = '';

	if (anomalyItems.length > 1) {
		let otherAnomalies = anomalyItems.slice(0, -1);
		description += otherAnomalies.map(({ name }) => name).join(', ') + ' and ';
	}

	let lastAnomaly = anomalyItems.at(-1);
	if (lastAnomaly) {
		description += lastAnomaly.name;
	}

	return description;
};

const formatNumberRange = (numbers: (number | string)[]): string => {
	if (numbers) {
		if (numbers.length > 1) {
			const sortedNumbers = _.orderBy(numbers, (digit) => +digit, 'asc');
			const summary = [];

			let start = sortedNumbers[0];
			let end = sortedNumbers[0];

			for (let i = 0; i < sortedNumbers.length; i++) {
				if (sortedNumbers[i + 1]) {
					if (+sortedNumbers[i] + 1 === +sortedNumbers[i + 1]) {
						end = sortedNumbers[i + 1];
					} else {
						summary.push({ start, end });

						start = sortedNumbers[i + 1];
						end = sortedNumbers[i + 1];
					}
				} else {
					summary.push({ start, end });
				}
			}

			if (summary.length === 0) {
				summary.push({ start, end });
			}

			return summary.map(({ start, end }) => (start === end ? start : `${start} - ${end}`)).join(', ');
		}

		return numbers.map((digit) => digit).join(', ');
	}

	return '';
};

const getAnomalyTypeClassFromAnomaly = (anomaly: string) => {
	for (const key of Object.keys(ANOMALY_TYPE)) {
		const anomalyType = ANOMALY_TYPE[key as keyof typeof ANOMALY_TYPE];
		if (anomalyType.ID === anomaly && anomalyType.CLASS) {
			return '( ' + anomalyType.CLASS + ' )';
		}
	}

	return '';
};

const getFileSizeValidationMessage = (fileSizeInBytes: number) => {
	return `The selected file is too large! Select a file less than ${convertBytesToMB(fileSizeInBytes)}MB`;
};

const getFilteredItemsFromFilters = <T>(filters: models.FilterItem[], data: T[]) => {
	if (filters.length > 0) {
		return data.filter((data) => {
			const isMatch = filters.every((filter) => {
				const value = _.get(data, filter.filterName);

				if (filter.filterMatchType === MATCH_TYPES.CONTAINS) {
					if (!value) {
						return false;
					}

					if (_.isArray(value)) {
						return value.some((valueItem) => compareSimilarValues(valueItem, filter.filterValue.toString()));
					}

					return compareSimilarValues(value, filter.filterValue.toString());
				}

				return matchPhrases(value, filter.filterValue.toString());
			});

			return isMatch;
		});
	}

	return data;
};

const getExecutiveSummary = (defaultSummary: string, executiveSummary: string, suffix: string) => {
	if (executiveSummary) {
		const suffixIndex = executiveSummary.indexOf(suffix);
		if (suffixIndex !== -1) {
			return defaultSummary + executiveSummary.split(suffix)[1];
		}

		return executiveSummary;
	}

	return defaultSummary;
};

const getIsImageFile = (source?: string) => {
	if (!source) return false;

	return IMAGE_UPLOAD_FORMATS.map((type) => getFileTypeAffix(type)).includes(source);
};

const getModuleLineNumbersFromProject = (projectDetails: models.Project): string[] => {
	let { facility, module } = projectDetails;
	let inspectableItems: string[] = [];

	if (module === EXTENDABLE_MODULES.ADHOC) {
		inspectableItems = facility.modules.flatMap((facilityModule) => facilityModule.inspectableItems || []);
	} else {
		const moduleMatch = facility.modules.find((facilityModule) => facilityModule.name === module);
		if (moduleMatch) inspectableItems = moduleMatch.inspectableItems || [];
	}

	return inspectableItems;
};

const validateAnomalyDistribution = (checklistItems: models.ChecklistItem[], anomalyItems: MappedAnomalyOption[]): string => {
	const selectedAnomalyItems = _.uniq(checklistItems.flatMap((item) => item.anomalyItems).map((anomaly) => anomaly.id.toString()));
	const allAnomalyItems = anomalyItems.map((anomaly) => anomaly.anomalyId);

	const anomaliesMissing = _.difference(allAnomalyItems, selectedAnomalyItems);

	if (anomaliesMissing.length > 0) {
		return `Missing anomalies for ${anomaliesMissing.join(', ')}`;
	}

	return '';
};

const getSomeChecklistItemsMissing = (checklistItems: models.ChecklistItem[], anomalyItems: MappedAnomalyOption[]): string => {
	const someChecklistMissing = checklistItems.some((checklistItem) => {
		return !checklistItem.assessment
	});

	if (someChecklistMissing) {
		return 'At least one checklist item not completed';
	}

	return validateAnomalyDistribution(checklistItems, anomalyItems);
};

const preventImageCaching = (src: string) => {
	if (src) {
		// if (src.indexOf(getS3BaseURL()) !== -1) {
		// 	const updatedURL = src.replace(getS3BaseURL(), `${process.env.REACT_APP_OFFICIAL_URL}`);
		// 	return updatedURL;
		// }

		if (config.preventImageCaching) {
			return src + '?last_fetched=' + Date.now();
		}
	}

	return src;
};

const getMatchingAnomalyClass = (anomalyItem: models.NewAnomalyItem) => {
	let classMatch = '';

	for (const key of Object.keys(DAMAGE_CLASS_TO_RISK_FACTOR)) {
		if (DAMAGE_CLASS_TO_RISK_FACTOR[key as keyof typeof DAMAGE_CLASS_TO_RISK_FACTOR].classes.includes(+anomalyItem.class)) {
			classMatch = key;
			break;
		}
	}

	return classMatch;
};

const mapAnomalyItemsToPCNLAnomaly = (pcnlItem: models.PCNLItemModel) => {
	const anomalyItems = pcnlItem.anomalyItems.map((anomalyItem: any) => ({
		riskFactor: getMatchingAnomalyClass(anomalyItem),
		..._.omit(anomalyItem, ['class']),
	}));

	return { ...pcnlItem, anomalyItems };
};

const reArrangeInspectionItemAnomalies = (inspectedItems: { id: number; checklistItems?: models.ChecklistItem[]; anomalyItems: (models.NewAnomalyItem | models.PCNLAnomalyItem)[] }[]) => {
	let anomalyId = 1;
	let inspectedItemId = 1;

	let updatedInspectedItems = inspectedItems.map((inspectedItem) => {
		const updatedInspectedItem = { ...inspectedItem, id: inspectedItemId };
		inspectedItemId += 1;

		const anomalyItems = inspectedItem.anomalyItems.map((anomalyItem) => {
			const newAnomalyItem = { ...anomalyItem, previousId: anomalyItem.id, id: anomalyId };
			anomalyId += 1;

			return newAnomalyItem;
		});

		if (inspectedItem.checklistItems) {
			const checklistItems = inspectedItem.checklistItems.map((checklistItem) => {
				const checklistAnomalyItems = checklistItem.anomalyItems.map((anomalyItem, anomalyIndex) => {
					const newAnomaly = anomalyItems.find((anomaly) => matchPhrases(anomaly.previousId.toString(), anomalyItem.id.toString()));

					if (newAnomaly) return {
						anomalyId: getAnomalyId(inspectedItem.id, anomalyIndex + 1),
						id: newAnomaly.id,
						name: newAnomaly.id.toString(),
					};

					return anomalyItem;
				});

				return { ...checklistItem, anomalyItems: checklistAnomalyItems };
			});

			updatedInspectedItem.checklistItems = checklistItems;
		}

		return { ...updatedInspectedItem, anomalyItems: anomalyItems as any };
	});

	return updatedInspectedItems;
};

const reArrangeAnomalies = <T>(anomalyItems: T[]) => {
	let anomalyId = 1

	const updatedAnomalyItems = anomalyItems.map((anomalyItem) => {
		const newAnomalyItem = { ...anomalyItem, id: anomalyId };
		anomalyId += 1;

		return newAnomalyItem;
	});

	return updatedAnomalyItems;
};

const reArrangeGeneralPhotos = (inspectedItems: { generalPhotos: models.NewAnomalyItem[] }[]) => {
	if (config.enableNewImageOrdering) {
		let updatedInspectedItems = inspectedItems.map((inspectedItem) => ({
			...inspectedItem,
			generalPhotos: inspectedItem.generalPhotos.map((generalPhoto, index) => ({ ...generalPhoto, refNo: index + 1 }))
		}));

		return updatedInspectedItems;
	}

	return inspectedItems;
};

const reArrangePhotoRecord = (photoRecords: models.PDFImageProps[]) => {
	let uniquePhotoRecords = _.uniqBy(photoRecords, 'src');

	if (config.enableNewImageOrdering) {
		return uniquePhotoRecords.map((record, index) => ({
			src: record.src,
			format: record.format,
			caption: record.caption.trim(),
			refNo: index + 1
		}));
	}

	return _.orderBy(uniquePhotoRecords, ['refNo'], 'asc');
};

const reArrangeVIPhotos = (viItems: models.VIReportItem[]) => {
	let updatedPipingItems = viItems.map((viItem) => ({
		...viItem, images: viItem.images.map((image, index) => ({ ...image, refNo: index + 1 }))
	}));

	return updatedPipingItems;
};

const removeCachedItems = () => {
	const exemptedKeys = [process.env.REACT_APP_AUTH_KEY, OFFLINE_TOKEN]
	for (let i = 0; i < localStorage.length; i++) {
		const storageKey = localStorage.key(i);
		if (storageKey && !exemptedKeys.includes(storageKey)) {
			localStorage.removeItem(storageKey);
		}
	}
}

const retrieveLineNumbersFromPipingItems = (pipingItems: models.FVIPipingItem[]) => {
	return pipingItems.map((pipingItem) => pipingItem.lineNumber);
};

const retrieveEquipmentNumbersFromEquipmentItems = (equipmentItems: models.FVIEquipmentItem[]) => {
	return equipmentItems.map((equipmentItem) => equipmentItem.equipmentNumber);
};

const splitTextIntoChunks = (text: string, size: number) => {
	const updatedText = text.replaceAll('<p><br></p>', '');
	if (updatedText.length < size) return [updatedText];

	const numberOfChunks = Math.ceil(updatedText.length / size);
	const textChunks = [];

	for (let index = 0, offset = 0; index < numberOfChunks; index++, offset += size) {
		const wordRange = updatedText.substring(offset, size + offset).trim();
		textChunks.push(wordRange);
	}

	return textChunks;
};

const storeItemInStorage = (key: string, value: any) => {
	try {
		localStorage.setItem(key, JSON.stringify(value));
	} catch (error) {
		removeCachedItems();
	}
}

const chunkRiskAssessmentPerPage = (pcnlItem: models.PCNLItemModel) => {
	let assessmentGroup: models.PCNLRiskAssessmentItem[][] = [];
	let assessmentSubgroup: models.PCNLRiskAssessmentItem[] = [];
	let maxCount = MAX_PAGE_WORD_COUNT;

	for (const assessment of pcnlItem.riskAssessment) {
		const wordCount = assessment.hazardDescription.length > assessment.controlsRequired.length ? assessment.hazardDescription.length : assessment.controlsRequired.length;
		maxCount -= wordCount;

		if (maxCount >= 0) {
			assessmentSubgroup.push(assessment);
		} else {
			assessmentGroup.push(assessmentSubgroup);
			assessmentSubgroup = [assessment];

			maxCount = MAX_PAGE_WORD_COUNT;
		}
	}

	if (assessmentSubgroup.length > 0) {
		assessmentGroup.push(assessmentSubgroup);
	}

	return assessmentGroup;
};

const getUpdatedPipingAnomalySummary = (isNewFormat: boolean, initialAnomalySummary: models.AnomalySummary, anomalyItems: models.NewAnomalyItem[]) => {
	if (!isNewFormat) {
		return;
	}

	const newAnomalySummary = {
		pipeBody: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		noOfPoints: 0,
		pitting: initialAnomalySummary.pitting,
		noOfPittingLocations: initialAnomalySummary.noOfPittingLocations,
		flanges: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		noOfFlanges: 0,
		fasteners: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		valves: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		noOfValves: 0,
		corrosionUnderPipeSupport: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		noOfCups: 0,
		compositeWrapOnLine: initialAnomalySummary.compositeWrapOnLine,
		noOfWrapPoints: initialAnomalySummary.noOfWrapPoints,
		conditionOfSmallBorePipings: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		noOfSmallBorePipings: 0,
		others: initialAnomalySummary.others,
		description: initialAnomalySummary.description,
		extentOfCoatingBreakdown: initialAnomalySummary.extentOfCoatingBreakdown,
	};

	const locations = anomalyItems.map((anomaly) => anomaly.location.toLowerCase().split(', ')).flat();
	const groupedLocations = _.groupBy(locations);

	for (const key in groupedLocations) {
		const keySwitch = key.toLowerCase().trim();
		switch (keySwitch) {
			case PIPING_SUMMARY_KEY.PIPE_BODY.toLowerCase(): {
				newAnomalySummary.pipeBody = 'Anomaly'
				newAnomalySummary.noOfPoints = groupedLocations[key].length;


				break;
			}

			case PIPING_SUMMARY_KEY.FLANGE.toLowerCase(): {
				newAnomalySummary.flanges = 'Anomaly';
				newAnomalySummary.noOfFlanges = groupedLocations[key].length;

				break;
			}

			case PIPING_SUMMARY_KEY.PIPE_SUPPORT.toLowerCase(): {
				newAnomalySummary.fasteners = 'Anomaly';

				break;
			}

			case PIPING_SUMMARY_KEY.UNDER_PIPE_SUPPORT.toLowerCase(): {
				newAnomalySummary.corrosionUnderPipeSupport = 'Anomaly'
				newAnomalySummary.noOfCups = groupedLocations[key].length;

				break;
			}

			case PIPING_SUMMARY_KEY.VALVE.toLowerCase(): {
				newAnomalySummary.valves = 'Anomaly';
				newAnomalySummary.noOfValves = groupedLocations[key].length;

				break;
			}

			case PIPING_SUMMARY_KEY.SMALL_BORE_PIPE.toLowerCase(): {
				newAnomalySummary.conditionOfSmallBorePipings = 'Anomaly'
				newAnomalySummary.noOfSmallBorePipings = groupedLocations[key].length;

				break;
			}

			default:
				if (keySwitch.includes('pipe body') || keySwitch.includes('elbow')) {
					newAnomalySummary.noOfPoints += groupedLocations[key].length;
				}

				break;
		}
	}

	return newAnomalySummary;
};

const getUpdatedEquipmentAnomalySummary = (isNewFormat: boolean, initialAnomalySummary: models.EquipmentAnomalySummaryItem, anomalyItems: models.NewAnomalyItem[]) => {
	if (!isNewFormat) {
		return null;
	}

	const ANOMALY_SUMMARY_MAPPING = {
		[EQUIPMENT_SUMMARY_KEY.BODY.toLowerCase()]: {
			key: 'pipeBody',
			count: 'noOfPoints'
		},
		[EQUIPMENT_SUMMARY_KEY.FLANGE.toLowerCase()]: {
			key: 'flanges',
		},
		[EQUIPMENT_SUMMARY_KEY.FASTENERS.toLowerCase()]: {
			key: 'fasteners',
		},
		[EQUIPMENT_SUMMARY_KEY.SUPPORT.toLowerCase()]: {
			key: 'supportCondition',
		},
		[EQUIPMENT_SUMMARY_KEY.UNDER_SUPPORT.toLowerCase()]: {
			key: 'corrosionUnderPipeSupport',
			count: 'noOfCups',
		},
		[EQUIPMENT_SUMMARY_KEY.VALVE.toLowerCase()]: {
			key: 'valves',
		},
		[EQUIPMENT_SUMMARY_KEY.VALVE.toLowerCase()]: {
			key: 'valves',
		},
		[EQUIPMENT_SUMMARY_KEY.PIPE.toLowerCase()]: {
			key: 'pipe',
			count: 'noOfPipe',
		},
		[EQUIPMENT_SUMMARY_KEY.GUAGE.toLowerCase()]: {
			key: 'guage',
			count: 'noOfGuage',
		},
		[EQUIPMENT_SUMMARY_KEY.DISH.toLowerCase()]: {
			key: 'dish',
			count: 'noOfDish',
		},
		[EQUIPMENT_SUMMARY_KEY.TOP_DISH.toLowerCase()]: {
			key: 'topDish',
			count: 'noOfTopDish',
		},
		[EQUIPMENT_SUMMARY_KEY.BOTTOM_DISH.toLowerCase()]: {
			key: 'bottomDish',
			count: 'noOfBottomDish',
		},
		[EQUIPMENT_SUMMARY_KEY.NOZZLE.toLowerCase()]: {
			key: 'nozzle',
			count: 'noOfNozzle',
		},
		[EQUIPMENT_SUMMARY_KEY.NAME_PLATE.toLowerCase()]: {
			key: 'namePlate',
			count: 'noOfNamePlate',
		},
		[EQUIPMENT_SUMMARY_KEY.SKIRT.toLowerCase()]: {
			key: 'skirt',
			count: 'noOfSkirt',
		},
		[EQUIPMENT_SUMMARY_KEY.STRUCTURE.toLowerCase()]: {
			key: 'structure',
			count: 'noOfStructure',
		},
		[EQUIPMENT_SUMMARY_KEY.EXTERNAL_PROTECTION.toLowerCase()]: {
			key: 'externalProtection',
			count: 'noOfExternalProtection',
		},
		[EQUIPMENT_SUMMARY_KEY.INSULATION.toLowerCase()]: {
			key: 'insulation',
			count: 'noOfInsulation',
		},
	};

	const newAnomalySummary = {
		pipeBody: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		noOfPoints: 0,
		pitting: initialAnomalySummary.pitting,
		noOfPittingLocations: initialAnomalySummary.noOfPittingLocations,
		flanges: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		fasteners: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		valves: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		corrosionUnderPipeSupport: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		noOfCups: 0,
		compositeWrapOnLine: initialAnomalySummary.compositeWrapOnLine,
		supportCondition: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		others: initialAnomalySummary.others,
		description: initialAnomalySummary.description,
		extentOfCoatingBreakdown: initialAnomalySummary.extentOfCoatingBreakdown,
		pipe: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		guage: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		dish: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		topDish: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		bottomDish: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		nozzle: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		namePlate: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		skirt: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		structure: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		externalProtection: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		insulation: ANOMALY_SUMMARY_OPTIONS.NO_ANOMALY,
		noOfPipe: 0,
		noOfGuage: 0,
		noOfDish: 0,
		noOfTopDish: 0,
		noOfBottomDish: 0,
		noOfNozzle: 0,
		noOfNamePlate: 0,
		noOfSkirt: 0,
		noOfStructure: 0,
		noOfExternalProtection: 0,
		noOfInsulation: 0,
	};

	const locations = anomalyItems.map((anomaly) => anomaly.location.split(', ')).flat();
	const groupedLocations = _.groupBy(locations);

	for (const location in groupedLocations) {
		const summary = ANOMALY_SUMMARY_MAPPING[location.toLowerCase().trim()];

		if (summary) {
			const { key, count } = summary;

			if (key) {
				_.set(newAnomalySummary, key, 'Anomaly');
			}

			if (count) {
				_.set(newAnomalySummary, count, groupedLocations[location].length);
			}
		}
	}

	return newAnomalySummary;
};

const transformListToOptions = (list: (string | number)[]) => {
	return list.map((option) => ({ id: option, name: option }));
};

const validateFileCaption = (value: string, helpers: Joi.CustomHelpers<any>) => {
	if (value.length === 0 || getFileCaptionIsInvalid(value)) {
		return helpers.error('any.invalid');
	}

	if (!getPrefixNotFoundInSingleFile(value)) {
		return helpers.error('any.invalid');
	}

	return value;
};

export {
	calculateWallLossPercentage,
	calculateWallLossValue,
	chunkRiskAssessmentPerPage,
	checkImageIsBase64,
	checkAnomalyChecklistComplete,
	checkTemplateIsSupported,
	checkPIDSupportedForInspection,
	checkItemIsArray,
	compareSimilarValues,
	concatClassNames,
	copyUpdatedData,
	convertBytesToMB,
	getMaximumDate,
	getMinimumDate,
	extractBase64String,
	formatDigits,
	formatTime,
	formatNumberRange,
	formatAnomalyText,
	formatTimeWithRelativeStamp,
	formatAddedDaysFromTimestamp,
	formatUniquePublicID,
	getChunkedAnomalySummaryItems,
	getCanCreateReport,
	getFileCaptionIsInvalid,
	getIsAfter,
	getIsExcelFile,
	getIsPDFFile,
	getIsSameOrAfter,
	getAnomalyFieldColor,
	getIsSameOrBefore,
	getModuleLineNumbersFromProject,
	getAnomalyStartNumber,
	getUpdatedPipingAnomalySummary,
	getUpdatedEquipmentAnomalySummary,
	getDaysBeforeFromCurrentDate,
	getChartLabelFromContext,
	getExtractedInspectionName,
	getFileUploadTypes,
	getInternalReviewDetails,
	getValueRange,
	getFileSizeValidationMessage,
	getAnomalyClassStyle,
	getMaxAnomalyType,
	getExecutiveSummary,
	getFileTypeAffix,
	getFileCaptionsUnique,
	getPrefixNotFoundInMultipleFiles,
	getPrefixNotFoundInSingleFile,
	getPCNLRiskFactorClassification,
	getInitials,
	getIsExpired,
	getIsAnomalyType,
	getTimeDifference,
	getLabelFromInspectedItems,
	getExecutiveSummaryAffix,
	getRelativeTime,
	getPageDetails,
	getRiskRatingData,
	getRiskLevelFromName,
	getStyleFromRisk,
	saveImage,
	getTotalScanLength,
	paginate,
	getChunkedData,
	getPrefixedModule,
	getPCNLAnomalyStyle,
	getMaxRatingFromInspectedItem,
	getItemStyle,
	getValueToDecimalPlaces,
	getOptimizedImageURL,
	getCachedDataFromException,
	getCriticalityFromRating,
	getConcreteDataRange,
	getDateFromString,
	getIsAnomalyWithCMD,
	getIsBefore,
	getRatingIsMajor,
	getRatingIsMinor,
	getRatingIsModerate,
	getRandomColor,
	getAnomalyTypeClassFromAnomaly,
	getChunkedAnomalies,
	getShowLastLineOption,
	getSomeChecklistItemsMissing,
	getMultipleFileCaptionsValid,
	getStringFromAnomalyDescription,
	getMaxClassFromInspectedItem,
	getPageRange,
	getYearFromDate,
	getSplitData,
	getPIDImages,
	getErrorMessage,
	getIsImageFile,
	getWallThicknessValue,
	getFilteredItemsFromFilters,
	getNumberOfMeasurementsForPECScan,
	getMultipleFileCaptionsNotEmpty,
	getLNGAnomalyClassStyle,
	getAnomalyId,
	getAnomalyTypeStyle,
	getItemsChunkedData,
	getUniqueItems,
	mapAnomalyItems,
	matchObjects,
	matchPhrases,
	mapAnomalyItemsToPCNLAnomaly,
	preventImageCaching,
	removeAccessToken,
	renderCell,
	renderStyle,
	reArrangeAnomalies,
	reArrangeInspectionItemAnomalies,
	reArrangeGeneralPhotos,
	reArrangeVIPhotos,
	reArrangePhotoRecord,
	removeCachedItems,
	retrieveLineNumbersFromPipingItems,
	retrieveEquipmentNumbersFromEquipmentItems,
	removeKeywordFromReference,
	replaceKeywordInReference,
	saveJSONFile,
	splitTextIntoChunks,
	sortTMLReadings,
	summarize,
	storeItemInStorage,
	transformCountry,
	transformCityToOption,
	transformDivisionItem,
	transformStateToOption,
	transformPageRange,
	transformInspectionType,
	transformFacilityItem,
	transformSelectOptions,
	transformProjectItem,
	transformRoleItem,
	transformUserItem,
	transformPlaceSearchResult,
	toTitleCase,
	transformListToOptions,
	validateFileLength,
	validateFileSize,
	validateFileType,
	validateWorkOrderNumber,
	validateAnomalyDistribution,
	getRiskFactorEquivaluent,
	validateFileCaption
};
